import { useCallback, useEffect, useRef, useState } from 'react';

import { classNames } from '../../libs/classNames';

import * as styles from './Dice.module.css';

export type IDiceFace = 1 | 2 | 3 | 4 | 5 | 6;

export interface IDiceProps {
  targetFace: IDiceFace | null;
  soundSrc?: string;
  onRollComplete?: () => void;
  rollTrigger: number;
  className?: string;
}

const FACE_CLASS_MAP: Record<IDiceFace, string> = {
  1: styles.dice_box__face1,
  2: styles.dice_box__face2,
  3: styles.dice_box__face3,
  4: styles.dice_box__face4,
  5: styles.dice_box__face5,
  6: styles.dice_box__face6,
};

const ROLL_DURATION_MS = 1000;

const waitForFrames = async (frames: number) => {
  for (let i = 0; i < frames; i += 1) {
    await new Promise<void>((resolve) =>
      requestAnimationFrame(() => resolve())
    );
  }
};

export const Dice = ({
  targetFace,
  soundSrc,
  onRollComplete,
  rollTrigger,
  className,
}: IDiceProps) => {
  const [hasRolled, setHasRolled] = useState(false);
  const [disableTransition, setDisableTransition] = useState(false);
  const [currentFace, setCurrentFace] = useState<IDiceFace | null>(null);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const prevTriggerRef = useRef(rollTrigger);

  useEffect(() => {
    if (soundSrc === undefined) {
      return;
    }

    audioRef.current = new Audio(soundSrc);
  }, [soundSrc]);

  const doRoll = useCallback(
    async (face: IDiceFace) => {
      setDisableTransition(true);
      setCurrentFace(null);

      await waitForFrames(2);

      setDisableTransition(false);
      setCurrentFace(face);

      if (hasRolled === false) {
        setHasRolled(true);
      }

      if (soundSrc !== undefined && audioRef.current) {
        audioRef.current.currentTime = 0;
        audioRef.current.play().catch(() => {});
      }

      setTimeout(() => {
        onRollComplete?.();
      }, ROLL_DURATION_MS);
    },
    [soundSrc, hasRolled, onRollComplete]
  );

  useEffect(() => {
    if (rollTrigger !== prevTriggerRef.current && targetFace !== null) {
      prevTriggerRef.current = rollTrigger;
      doRoll(targetFace);
    }
  }, [rollTrigger, targetFace, doRoll]);

  const diceClassName = classNames(styles.dice, {
    [styles.dice__disabledAnimation]: hasRolled,
  });

  const boxClassName = classNames(styles.dice_box, {
    [styles.dice_box__disableTransition]: disableTransition,
    ...(currentFace !== null ? { [FACE_CLASS_MAP[currentFace]]: true } : {}),
  });

  return (
    <div className={classNames(diceClassName, className)}>
      <div className={boxClassName}>
        <div className={classNames(styles.dice_side, styles.dice_side__front)}>
          <span
            className={classNames(styles.dice_dot, styles.dice_dot__center)}
          />
        </div>

        <div className={classNames(styles.dice_side, styles.dice_side__back)}>
          {/* верхние */}
          <span
            className={classNames(
              styles.dice_dot,
              styles.dice_dot__top,
              styles.dice_dot__left
            )}
          />
          <span
            className={classNames(
              styles.dice_dot,
              styles.dice_dot__top,
              styles.dice_dot__right
            )}
          />

          {/* центральные (слева и справа, БЕЗ center класса!) */}
          <span
            className={classNames(styles.dice_dot, styles.dice_dot__left)}
            style={{ marginTop: '39%' }}
          />
          <span
            className={classNames(styles.dice_dot, styles.dice_dot__right)}
            style={{ marginTop: '39%' }}
          />

          {/* нижние */}
          <span
            className={classNames(
              styles.dice_dot,
              styles.dice_dot__bottom,
              styles.dice_dot__left
            )}
          />
          <span
            className={classNames(
              styles.dice_dot,
              styles.dice_dot__bottom,
              styles.dice_dot__right
            )}
          />
        </div>

        <div className={classNames(styles.dice_side, styles.dice_side__right)}>
          <span
            className={classNames(
              styles.dice_dot,
              styles.dice_dot__top,
              styles.dice_dot__left
            )}
          />
          <span
            className={classNames(styles.dice_dot, styles.dice_dot__center)}
          />
          <span
            className={classNames(
              styles.dice_dot,
              styles.dice_dot__bottom,
              styles.dice_dot__right
            )}
          />
        </div>

        <div className={classNames(styles.dice_side, styles.dice_side__left)}>
          <span
            className={classNames(
              styles.dice_dot,
              styles.dice_dot__top,
              styles.dice_dot__left
            )}
          />
          <span
            className={classNames(
              styles.dice_dot,
              styles.dice_dot__top,
              styles.dice_dot__right
            )}
          />
          <span
            className={classNames(
              styles.dice_dot,
              styles.dice_dot__bottom,
              styles.dice_dot__left
            )}
          />
          <span
            className={classNames(
              styles.dice_dot,
              styles.dice_dot__bottom,
              styles.dice_dot__right
            )}
          />
        </div>

        <div className={classNames(styles.dice_side, styles.dice_side__top)}>
          <span
            className={classNames(
              styles.dice_dot,
              styles.dice_dot__top,
              styles.dice_dot__left
            )}
          />
          <span
            className={classNames(
              styles.dice_dot,
              styles.dice_dot__top,
              styles.dice_dot__right
            )}
          />
          <span
            className={classNames(styles.dice_dot, styles.dice_dot__center)}
          />
          <span
            className={classNames(
              styles.dice_dot,
              styles.dice_dot__bottom,
              styles.dice_dot__left
            )}
          />
          <span
            className={classNames(
              styles.dice_dot,
              styles.dice_dot__bottom,
              styles.dice_dot__right
            )}
          />
        </div>

        <div className={classNames(styles.dice_side, styles.dice_side__bottom)}>
          <span
            className={classNames(
              styles.dice_dot,
              styles.dice_dot__top,
              styles.dice_dot__left
            )}
          />
          <span
            className={classNames(
              styles.dice_dot,
              styles.dice_dot__bottom,
              styles.dice_dot__right
            )}
          />
        </div>

        <div
          className={classNames(styles.dice_inner, styles.dice_inner__front)}
        />
        <div
          className={classNames(styles.dice_inner, styles.dice_inner__back)}
        />
        <div
          className={classNames(styles.dice_inner, styles.dice_inner__right)}
        />
        <div
          className={classNames(styles.dice_inner, styles.dice_inner__left)}
        />
        <div
          className={classNames(styles.dice_inner, styles.dice_inner__top)}
        />
        <div
          className={classNames(styles.dice_inner, styles.dice_inner__bottom)}
        />

        <div className={styles.dice_cover} />
        <div className={classNames(styles.dice_cover, styles.dice_cover__x)} />
        <div className={classNames(styles.dice_cover, styles.dice_cover__z)} />
      </div>
    </div>
  );
};
